import { Head, router, usePage } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  newServicePurchasePath,
  newServiceReviewPath,
  rootPath,
  serviceEditorPath,
  servicePath,
  servicePurchasesPath,
} from "@travily/routes";
import type ServicesShow from "@travily/types/pages/ServicesShow";
import { HeartToggleButton } from "@ui/components/HeartToggleButton";
import { Icon } from "@ui/components/Icon";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { Button } from "@ui/primitives/Button";
// Services reuse the products ShowView — the surface is structurally identical
// (title, photos, price, rating, reviews). Any service-specific divergence
// (scheduling, slot booking) will graduate into a separate view when it lands.
import { ShowView } from "@/components/ProductShowView/ShowView";
import { useFavorite } from "@/hooks/use-favorite";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import { buildShowViewProps } from "@/lib/build-show-view-props";
import { wrapReviewsInScroll } from "@/lib/reviews-scroll";
import { useListingAnalytics } from "@/lib/use-listing-analytics";
import type { SharedProps } from "@/types";

const ServiceShowPage = ({ service, creator, reviews, can }: ServicesShow) => {
  const { current_user } = usePage<SharedProps>().props;
  const { trackCheckout } = useListingAnalytics(service, "service", creator.id);
  const isAuthenticated = Boolean(current_user);
  const isGuestCheckout = !isAuthenticated && service.can.purchase;
  const manageHref = service.can.manage ? serviceEditorPath(service.sqid) : undefined;

  const displayTitle = service.title ?? "";
  const { share, shared } = useShare();
  const { visitModal } = useModalStack();
  const onBack = useNavigateBack(rootPath());
  const favorite = useFavorite({
    favorited: service.favorited,
    favorableType: "Listing",
    favorableId: service.sqid,
  });

  const handleShare = () => {
    share({
      title: displayTitle,
      text: t("web.services.show.share_text", { title: displayTitle }),
      url: `${location.origin}${servicePath(service.sqid)}`,
    });
  };

  const handlePurchase = () => {
    trackCheckout();
    if (isGuestCheckout) {
      visitModal(newServicePurchasePath(service.sqid));
      return;
    }
    router.post(servicePurchasesPath(service.sqid));
  };

  const showViewProps = buildShowViewProps("service", service, creator, reviews);

  return (
    <PageFrame
      mobileTopBar={
        <TopBarMobile
          onBack={onBack}
          title={displayTitle}
          actions={
            <>
              <HeartToggleButton
                aria-label={t("common.actions.favorite")}
                isSelected={favorite.isFavorited}
                onChange={favorite.toggle}
                size="24"
              />
              <Button aria-label={t("common.actions.share")} onPress={handleShare}>
                <Icon name="share" size="20" type="sticker" />
              </Button>
            </>
          }
        />
      }
    >
      <Head title={t("web.services.show.meta_title", { title: displayTitle })}>
        <meta name="description" content={t("web.services.show.meta_description", { title: displayTitle })} />
      </Head>
      <ShowView
        {...showViewProps}
        favorited={favorite.isFavorited}
        manageHref={manageHref}
        onAddReview={can.create_review ? () => visitModal(newServiceReviewPath(service.sqid)) : undefined}
        onFavoriteToggle={favorite.toggle}
        onPurchase={service.can.purchase ? handlePurchase : undefined}
        onShare={handleShare}
        shared={shared}
        reviewsListWrapper={wrapReviewsInScroll}
      />
    </PageFrame>
  );
};

export default ServiceShowPage;
